import { LoadingSpinner } from "components/LoadingSpinner";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import useFormValidation from "hooks/useFormValidation";
import { usePayerInformation } from "hooks/usePayerInformation";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { isEmpty } from "lodash";
import { handleChangeDateWithMask } from "pages/CreateOrder/utils/mask";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { useGetAccountByIdQuery } from "store/queries/account";
import styled from "styled-components";
import { IGFEServiceDetails } from "types/Order/Order";

import { EligibilityButton } from "./components/EligibilityButton";
import { Message } from "./components/Message";
import { areAllDeductibleFieldsNull } from "./helpers/helper";

const StyledCard = styled(Card)<{ ordertypefield: string }>`
  margin-bottom: ${sizer(4)};
  display: ${({ ordertypefield }) =>
    ordertypefield === "GFE" ? "block" : "none"};
`;

export function PayerForm({
  redo = false,
  draft = false,
  copy = false,
}: {
  redo?: boolean;
  draft?: boolean;
  copy?: boolean;
}) {
  const {
    control,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const query = useQuery();
  const accountId = query.get("accountId") || "";
  const [eligibilityMessage, setEligibilityMessage] = useState<string>("");
  const [showMemberIdAndEligibility, setShowMemberIdAndEligibility] =
    useState<boolean>(true);

  const { selectedDraftOrder: draftOrder } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { selectedOrderToRedo: orderToRedo } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { selectedOrderToCopy: orderToCopy } = useSelector(
    (state: RootState) => state.orderSlice
  );
  const { payersNameInfo, payersCodesInfo } = useSelector(
    (state: any) => state.orderSlice
  );

  const { data: accountData } = useGetAccountByIdQuery(accountId, {
    skip: !accountId,
  });

  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();

  const orderTypeField = watch("orderType");
  const payerNameField = watch("payerName");
  const patientFirstNameField = watch("firstName");
  const patientLastNameField = watch("lastName");
  const patientDOBField = watch("dateOfBirth");
  const memberIdField = watch("memberId");
  const practiceTypeField = watch("practiceType");
  const isSubscriberPatientCheckbox = watch("isSubscriberPatient");

  const { loadingPayersName } = usePayerInformation(accountId);

  const mainProvider = useMemo(() => {
    return (
      accountData?.data?.providers.find(
        (aMainProvider) => !!aMainProvider.mainFacility
      ) || {}
    );
  }, [accountData]);

  // FIX ME: BUGGY BUT YET NEEDED CODE TO DEFAULT VALUE FOR FETCHED PAYER NAME
  // SHOULD FIND OUT HOW TO SET THE DEFAULT VALUE FOR A DROPDOWN IN A REACT FORM
  // THE VALUE WILL BE SET AFTER IT FINISHES LOADING DATA.

  useEffect(() => {
    if (isEmpty(payerNameField) && !isEmpty(payersNameInfo)) {
      if (redo) {
        setValue(
          "payerName",
          (orderToRedo?.serviceDetails as IGFEServiceDetails)?.payerName ||
            payersNameInfo[0].value
        );
      } else if (copy) {
        setValue(
          "payerName",
          (orderToCopy?.serviceDetails as IGFEServiceDetails)?.payerName ||
            payersNameInfo[0].value
        );
      } else {
        setValue(
          "payerName",
          (draftOrder?.serviceDetails as IGFEServiceDetails)?.payerName ||
            payersNameInfo[0].value
        );
      }
    }
  }, [payersNameInfo, payerNameField, redo]);

  useEffect(() => {
    if (payerNameField === "Self-Pay Rate") {
      setShowMemberIdAndEligibility(false);
      setValue("deductible", 0);
      setValue("outOfPocketMax", 100000);
      setValue("flatCopay", 0);
      setValue("coinsurancePercent", 100);
    } else {
      setShowMemberIdAndEligibility(true);
    }
  }, [payerNameField]);

  useEffect(() => {
    const draftOrderCheck =
      draft &&
      (!draftOrder?.benefitDetails || !draftOrder?.benefitDetails?.response);
    const redoOrderCheck =
      redo &&
      (!orderToRedo?.benefitDetails || !orderToRedo?.benefitDetails?.response);
    const orderToCopyCheck =
      copy &&
      (!orderToCopy?.benefitDetails || !orderToCopy?.benefitDetails?.response);
    if (
      loadingPayersName ||
      draftOrderCheck ||
      redoOrderCheck ||
      orderToCopyCheck ||
      (!draft && !redo && !copy)
    ) {
      setValue("benefitDetails", null);
      return;
    }
    let order = orderToRedo;
    if (draft) {
      order = draftOrder;
    } else if (copy) {
      order = orderToCopy;
    }
    const providerNpi = mainProvider?.npi;
    const eligibilityEnabled = mainProvider?.eligibilityEnabled;
    const eligibilityRequest = order?.benefitDetails?.request;
    if (
      eligibilityRequest?.member?.firstName !== patientFirstNameField ||
      eligibilityRequest?.member?.lastName !== patientLastNameField ||
      eligibilityRequest?.member?.dob !== patientDOBField ||
      eligibilityRequest?.member?.memberID !== memberIdField ||
      eligibilityRequest?.practiceTypeCode !== practiceTypeField ||
      eligibilityRequest?.provider?.npi !== providerNpi ||
      eligibilityRequest?.isSubscriberPatient !== eligibilityEnabled
    ) {
      setValue("benefitDetails", null);
    } else {
      setValue("benefitDetails", order?.benefitDetails);
      if (redo || draft || copy) {
        const response = order?.benefitDetails?.response;
        if (response.planStatus === "Inactive") {
          setEligibilityMessage(
            "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
          );
        }
        const allFieldsAreNull =
          !!response?.details?.HBPC_Deductible_OOP_Summary ||
          areAllDeductibleFieldsNull(
            response.details?.HBPC_Deductible_OOP_Summary
          );

        if (response.isEligibilityEmpty || allFieldsAreNull) {
          setEligibilityMessage(response.additionalInfo);
        }
      }
    }
  }, [
    patientFirstNameField,
    patientLastNameField,
    patientDOBField,
    memberIdField,
    practiceTypeField,
    loadingPayersName,
    payersCodesInfo,
    draftOrder,
    orderToRedo,
    orderToCopy,
    mainProvider,
  ]);

  return (
    <>
      <StyledCard title="Payer" ordertypefield={orderTypeField}>
        <div className="flex flex-column gap-4 w-100">
          <div className="formgrid grid w-100">
            <div className="field flex flex-column col-12 md:col-12 lg:col-3">
              <Label htmlFor="payerName">Payer Name</Label>

              <Controller
                control={control}
                name="payerName"
                render={({ field: { onChange, value } }) =>
                  loadingPayersName ? (
                    <LoadingSpinner
                      customStyle={{ width: "50px", height: "100px" }}
                    />
                  ) : (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      placeholder="Any"
                      id="payerName"
                      data-testid="payerName_PayerForm"
                      options={payersNameInfo}
                      onChange={(e) => {
                        onChange(e.value);
                        if (redo) {
                          setValue("redoOrderFirstRender", false);
                        }
                        if (draft) {
                          setValue("draftOrderFirstRender", false);
                        }
                        if (copy) {
                          setValue("copyOrderFirstRender", false);
                        }
                      }}
                      showClear
                    />
                  )
                }
              />
            </div>

            {!!mainProvider.eligibilityEnabled && (
              <div className="field flex flex-column col-12 md:col-12 lg:col-3">
                <Label htmlFor="practiceType">Service Type</Label>

                <Controller
                  control={control}
                  name="practiceType"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      value={value || ""}
                      optionLabel="name"
                      data-testid="practiceType_PayerForm"
                      placeholder="Service Type"
                      id="practiceType"
                      options={mapPayerNameToCodeForDropdown}
                      onChange={onChange}
                      showClear
                    />
                  )}
                />
              </div>
            )}
          </div>
          {!!mainProvider.eligibilityEnabled && (
            <>
              <div className="formgrid grid w-100">
                {!isSubscriberPatientCheckbox && (
                  <>
                    <div className="field flex flex-column col-12 md:col-12 lg:col-3">
                      <Label htmlFor="subscriberFirstName">
                        Subscriber First Name
                      </Label>

                      <Controller
                        control={control}
                        name="subscriberFirstName"
                        render={({ field: { onChange, value, onBlur } }) => (
                          <>
                            <InputText
                              id="subscriberFirstName"
                              data-testid="subscriberFirstName_PayerForm"
                              value={value || ""}
                              autoComplete="off"
                              placeholder="Subscriber Name"
                              onBlur={() =>
                                handleOnBlurField({
                                  onBlur,
                                  field: "subscriberFirstName",
                                  trigger,
                                })
                              }
                              onChange={onChange}
                            />

                            {getFormErrorMessage("subscriberFirstName", errors)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field flex flex-column col-12 md:col-12 lg:col-3">
                      <Label htmlFor="subscriberLastName">
                        Subscriber Last Name
                      </Label>

                      <Controller
                        control={control}
                        name="subscriberLastName"
                        render={({ field: { onChange, value, onBlur } }) => (
                          <>
                            <InputText
                              id="subscriberLastName"
                              data-testid="subscriberLastName_PayerForm"
                              value={value || ""}
                              autoComplete="off"
                              placeholder="Subscriber Name"
                              onBlur={() =>
                                handleOnBlurField({
                                  onBlur,
                                  field: "subscriberLastName",
                                  trigger,
                                })
                              }
                              onChange={onChange}
                            />

                            {getFormErrorMessage("subscriberLastName", errors)}
                          </>
                        )}
                      />
                    </div>
                    <div className="field flex flex-column col-12 md:col-12 lg:col-3">
                      <Label htmlFor="subscriberDOB">Subscriber DOB</Label>

                      <Controller
                        name="subscriberDOB"
                        control={control}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <>
                            <InputText
                              value={value || ""}
                              id="subscriberDOB"
                              data-testid="subscriberDOB_PayerForm"
                              placeholder="99/99/9999"
                              onBlur={() =>
                                handleOnBlurField({
                                  onBlur,
                                  field: "subscriberDOB",
                                  trigger,
                                })
                              }
                              onChange={(e) =>
                                handleChangeDateWithMask(e, onChange)
                              }
                            />

                            {getFormErrorMessage("subscriberDOB", errors)}
                          </>
                        )}
                      />
                    </div>
                  </>
                )}
                {showMemberIdAndEligibility ? (
                  <div className="field flex flex-column col-12 md:col-12 lg:col-3">
                    <Label htmlFor="memberId">Member ID</Label>

                    <Controller
                      control={control}
                      name="memberId"
                      render={({ field: { onChange, value, onBlur } }) => (
                        <>
                          <InputText
                            id="memberId"
                            value={value || ""}
                            autoComplete="off"
                            data-testid="memberId_PayerForm"
                            placeholder="Member ID"
                            onBlur={() =>
                              handleOnBlurField({
                                onBlur,
                                field: "memberId",
                                trigger,
                              })
                            }
                            onChange={onChange}
                          />

                          {getFormErrorMessage("memberId", errors)}
                        </>
                      )}
                    />
                  </div>
                ) : null}
                <div className="field flex-column col-12 md:col-12 lg:col-3 hidden">
                  <Label htmlFor="groupNumber">Group Number</Label>

                  <Controller
                    control={control}
                    name="groupNumber"
                    render={({ field: { onChange, value, onBlur } }) => (
                      <>
                        <InputText
                          id="groupNumber"
                          data-testid="groupNumber_PayerForm"
                          value={value || ""}
                          autoComplete="off"
                          placeholder="Group Number"
                          onBlur={() =>
                            handleOnBlurField({
                              onBlur,
                              field: "groupNumber",
                              trigger,
                            })
                          }
                          onChange={onChange}
                        />

                        {getFormErrorMessage("groupNumber", errors)}
                      </>
                    )}
                  />
                </div>
              </div>
              {showMemberIdAndEligibility ? (
                <EligibilityButton
                  accountId={accountId}
                  mainProvider={mainProvider}
                  errorMessageSetter={setEligibilityMessage}
                  disabled={loadingPayersName}
                />
              ) : null}
            </>
          )}
        </div>
      </StyledCard>
      <Message eligibilityMessage={eligibilityMessage} />
    </>
  );
}
